import { getJWTToken } from '../../utils/auth';
import type { WidaStudent } from '../Upload/widaHistory';
import {
  DOMAINS,
  DOMAIN_LABELS,
  currentLevel,
  domainScores,
  isLtel,
  latestAdministrationLevel,
  levelChange,
  levelLabel,
  scoredAdministrations,
} from './derive';
import { authHeaders } from '../../core/impersonation';
import type { PlanCatalog } from './planRecordApi';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

const ATTENTION_LEVEL_CEILING = 4;

export type AccommodationInputs = {
  gradeBand: string;
  yearsInElBucket: string;
  compositeProficiencyLevel: string;
  attentionDomainNames: string[];
  isLtelStudent: boolean;
  hasFlashlightData: boolean;
  compositeProficiencyScore: number | null;
  compositeYearOverYearChange: number | null;
  scoredAdministrationsCount: number;
  domainScores: Record<string, number>;
};

function gradeBand(grade: string): string {
  const g = grade.trim().toUpperCase();
  if (g === 'K' || g === 'KG' || g === 'PK' || g === '0') return 'Grades K-2';
  const n = parseInt(g.replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(n)) return '';
  if (n <= 2) return 'Grades K-2';
  if (n <= 5) return 'Grades 3-5';
  if (n <= 8) return 'Grades 6-8';
  return 'Grades 9-12';
}

function yearsBucket(years: number | null): string {
  if (years == null) return '';
  if (years < 2) return 'Under 2 years';
  if (years <= 4) return '2-4 years';
  return '5 or more years';
}

export function deriveAccommodationInputs(student: WidaStudent): AccommodationInputs | null {
  const composite = currentLevel(student, 'composite');
  if (composite == null) return null;
  const attentionDomainNames = DOMAINS.filter((d) => {
    const level = currentLevel(student, d);
    return level != null && level < ATTENTION_LEVEL_CEILING;
  }).map((d) => DOMAIN_LABELS[d]);
  return {
    gradeBand: gradeBand(student.latestGrade ?? ''),
    yearsInElBucket: yearsBucket(student.yearsInEl),
    compositeProficiencyLevel: levelLabel(composite),
    attentionDomainNames,
    isLtelStudent: isLtel(student),
    hasFlashlightData: false,
    compositeProficiencyScore: latestAdministrationLevel(student, 'composite'),
    compositeYearOverYearChange:
      latestAdministrationLevel(student, 'composite') == null ? null : levelChange(student, 'composite'),
    scoredAdministrationsCount: scoredAdministrations(student, 'any'),
    domainScores: domainScores(student),
  };
}

export type SuggestedAccommodations = { accommodations: string[]; items: string[] };

export async function fetchSuggestedAccommodations(
  studentId: string,
  inputs: AccommodationInputs,
  catalog?: PlanCatalog | null,
  alternateAccess?: boolean,
): Promise<SuggestedAccommodations> {
  const token = await getJWTToken();
  const pickable = catalog != null && catalog.classroom.length + catalog.contentTests.length > 0;
  const body: Record<string, unknown> = { ...inputs };
  if (pickable) body.catalog = true;
  if (alternateAccess) body.alternateAccess = true;
  const response = await fetch(
    `${apiUrl}/students/${encodeURIComponent(studentId)}/accommodations`,
    {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw new Error(`accommodations fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as { accommodations?: string[]; items?: string[] };
  return {
    accommodations: Array.isArray(data.accommodations) ? data.accommodations : [],
    items: Array.isArray(data.items) ? data.items.filter((id): id is string => typeof id === 'string') : [],
  };
}
